import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { LoginFlow, RegistrationFlow, UiText } from '@ory/client';
import { firstValueFrom } from 'rxjs';
import { AuthRedirectService } from 'src/app/services/authentication/auth-redirect.service';
import {
  csrfToken,
  flowMessages,
  flowValue,
  offersInput,
  submitFlow,
} from 'src/app/services/authentication/kratos-flow';
import {
  ErrBrowserLocationChangeRequired,
  ErrTwoFARequired,
} from 'src/app/services/authentication/kratos.errors';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { CodeEntryComponent } from 'src/app/core/components/code-entry/code-entry.component';
import { codeStep } from 'src/app/core/components/code-entry/code-step';
import { LanguageSwitchComponent } from 'src/app/core/components/language-switch/language-switch.component';
import { CodeResendComponent } from '../components/code-resend/code-resend.component';
import { resendCooldown } from '../components/code-resend/resend-cooldown';

/** Kratos: an account with this identifier exists already. */
const ACCOUNT_EXISTS = 4000007;

type CodeFlow =
  | { kind: 'register'; flow: RegistrationFlow }
  | { kind: 'login'; flow: LoginFlow };

/**
 * "Check your email": the 6-digit code from sign-up or from login. The
 * mails link here as `/register/code?flow=…&code=…`, which submits right
 * away. A sign-up for an address that already has an account switches to
 * a login code, so nobody hits a dead end. The address shown comes from
 * the flow, never from the URL.
 */
@Component({
  selector: 'app-auth-code',
  templateUrl: './auth-code.component.html',
  imports: [
    RouterModule,
    TranslocoModule,
    LanguageSwitchComponent,
    CodeEntryComponent,
    CodeResendComponent,
  ],
})
export class AuthCodePageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(KratosAuthenticationService);
  private readonly authRedirect = inject(AuthRedirectService);
  private readonly translation = inject(TranslationService);

  protected readonly step = codeStep();

  protected readonly current = signal<CodeFlow | null>(null);

  protected readonly messages = signal<UiText[]>([]);

  /** The flow couldn't be loaded (expired, unknown, already used). */
  protected readonly lost = signal(false);

  /** A sign-up turned into a login: the address has an account already. */
  protected readonly switchedToLogin = signal(false);

  protected readonly cooldown = resendCooldown();

  protected readonly email = computed(() => {
    const current = this.current();
    if (!current) {
      return '';
    }
    return current.kind === 'register'
      ? flowValue(current.flow, 'traits.email')
      : flowValue(current.flow, 'identifier');
  });

  private inviteToken: string | null = null;

  /** Where the login page was asked to go afterwards (`returnTo`). */
  private returnTo: string | null = null;

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.inviteToken = params.get('invite');
    this.returnTo = params.get('returnTo');
    this.switchedToLogin.set(params.get('existing') === '1');
    this.load(params.get('flow'), params.get('code'));
  }

  /** The id names a sign-up or a login flow; the mails link both here. */
  private async load(flowId: string | null, code: string | null) {
    if (!flowId) {
      this.lost.set(true);
      return;
    }
    try {
      const flow = (await this.auth.getRegistrationFlow(
        flowId
      )) as RegistrationFlow;
      this.use({ kind: 'register', flow });
    } catch {
      try {
        this.use({ kind: 'login', flow: await this.auth.getLoginFlow(flowId) });
      } catch {
        this.lost.set(true);
        return;
      }
    }
    if (!offersInput(this.current()!.flow, 'code', 'code')) {
      this.lost.set(true);
      return;
    }
    this.cooldown.start();
    if (code) {
      this.step.code.setValue(code);
      this.submit(code);
    }
  }

  private use(current: CodeFlow): void {
    this.current.set(current);
    // the "code sent" info is what this page already says
    this.messages.set(
      flowMessages(current.flow).filter(message => message.type === 'error')
    );
  }

  protected async submit(code: string): Promise<void> {
    const current = this.current();
    if (!current) {
      return;
    }
    const result = await this.step.verify(async () => {
      const submission = await submitFlow<LoginFlow | RegistrationFlow, void>(
        () => this.verify(current, code)
      );
      // Kratos took the code but sends the browser on: to the second factor
      // (the account has one) or back to an OAuth2 client. Stays "Verifying…"
      // until the page unloads.
      if (
        !submission.ok &&
        submission.error instanceof ErrBrowserLocationChangeRequired
      ) {
        await firstValueFrom(
          this.authRedirect.redirect(submission.error.redirectUri)
        );
      }
      return submission;
    });
    if (!result) {
      return;
    }
    if (result.ok) {
      this.enterApp();
    } else if (result.error instanceof ErrBrowserLocationChangeRequired) {
      // still here: the redirect was cancelled, and the code is used up
      this.lost.set(true);
    } else if (result.error instanceof ErrTwoFARequired) {
      this.router.navigate(['/login'], { queryParams: { aal: 'aal2' } });
    } else if (
      current.kind === 'register' &&
      result.flow &&
      flowMessages(result.flow).some(message => message.id === ACCOUNT_EXISTS)
    ) {
      await this.step.run(() => this.switchToLogin());
    } else {
      if (result.flow) {
        this.use({ ...current, flow: result.flow } as CodeFlow);
      }
      this.step.reject();
    }
  }

  /** Sends the code; resolves once Kratos accepted it and a session exists. */
  private async verify(current: CodeFlow, code: string): Promise<void> {
    if (current.kind === 'register') {
      await this.auth.completeRegistrationFlowV2(current.flow, {
        method: 'code',
        code,
        csrf_token: csrfToken(current.flow),
        traits: { email: this.email() },
      });
      return;
    }
    // the login page records the login when it takes over (`enterApp`)
    await this.auth.completeLoginFlowV2(current.flow, 'code', {
      method: 'code',
      code,
      identifier: this.email(),
      csrf_token: csrfToken(current.flow),
    });
  }

  /**
   * The address has an account already: send it a login code instead and
   * stay on this page (as a login), so the sign-up never dead-ends.
   */
  private async switchToLogin(): Promise<void> {
    const email = this.email();
    try {
      const flow = await this.auth.createLoginFlow('aal1');
      const sent = await this.sendLoginCode(flow, email);
      this.router.navigate(['/register', 'code'], {
        queryParams: { flow: sent.id, existing: 1 },
        replaceUrl: true,
      });
      this.switchedToLogin.set(true);
      this.use({ kind: 'login', flow: sent });
      this.step.reset();
      this.cooldown.start();
    } catch {
      this.lost.set(true);
    }
  }

  /** Every account can sign in with a code, whatever login style Kratos runs. */
  private sendLoginCode(flow: LoginFlow, email: string) {
    return this.submitLogin(flow, {
      method: 'code',
      identifier: email,
      csrf_token: csrfToken(flow),
      transient_payload: this.transientPayload(flow.id),
    });
  }

  /** Login steps that re-render the flow come back as a 400 with it. */
  private async submitLogin(
    flow: LoginFlow,
    body: Record<string, unknown>
  ): Promise<LoginFlow> {
    const result = await submitFlow<LoginFlow, unknown>(() =>
      this.auth.completeLoginFlowV2(
        flow,
        body['method'] as string,
        body as never
      )
    );
    if (!result.ok && result.flow) {
      return result.flow;
    }
    throw result.ok ? new Error('login finished without a code') : result.error;
  }

  protected async resend(): Promise<void> {
    const current = this.current();
    if (!current || this.cooldown.remaining() > 0) {
      return;
    }
    const base = {
      method: 'code' as const,
      resend: 'code',
      csrf_token: csrfToken(current.flow),
      transient_payload: this.transientPayload(current.flow.id),
    };
    const result = await this.step.run(() =>
      submitFlow<LoginFlow | RegistrationFlow, unknown>(() =>
        current.kind === 'register'
          ? this.auth.completeRegistrationFlowV2(current.flow, {
              ...base,
              traits: { email: this.email() },
            })
          : this.auth.completeLoginFlowV2(current.flow, 'code', {
              ...base,
              identifier: this.email(),
            })
      )
    );
    if (!result) {
      return;
    }
    // Kratos answers a resend with the flow again (a 400)
    if (!result.ok && result.flow) {
      this.use({ ...current, flow: result.flow } as CodeFlow);
    }
    this.step.reset();
    this.cooldown.start();
  }

  /** Back to the form the address came from, with it filled in. */
  protected changeEmail(): void {
    const current = this.current();
    this.router.navigate([current?.kind === 'login' ? '/login' : '/register'], {
      state: { email: this.email() },
    });
  }

  private enterApp(): void {
    const current = this.current();
    // a new account starts onboarding; an invitee joins through it too
    if (current?.kind !== 'login' || this.inviteToken) {
      this.router.navigate(['/onboarding'], {
        queryParams: this.inviteToken ? { invite: this.inviteToken } : {},
      });
      return;
    }
    // a login finishes where a password login does: the login page checks
    // the session, confirms an OAuth2 challenge or goes to `returnTo`
    const loginChallenge = current.flow.return_to
      ? new URL(current.flow.return_to).searchParams.get('login_challenge')
      : null;
    this.router.navigate(['/login'], {
      queryParams: {
        check: 'whoami',
        login_challenge: loginChallenge ?? undefined,
        returnTo: this.returnTo ?? undefined,
      },
    });
  }

  private transientPayload(flowId: string) {
    return { flow_id: flowId, locale: this.translation.currentLocale };
  }
}
